import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useFormik } from '@fourthwall/utils/dist/formik'
import { fixRounding } from '@fourthwall/utils/dist/number'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { routing } from '@utils/routing'

import { useCreateImageResources } from '../../../../../../hooks/useCreateImageResources'
import { useAccountSettingsQuery } from '../../../../../Settings/hooks/useAccountSettingsQuery'
import { useCreateTierMutation } from '../../../../hooks/useCreateTierMutation'
import { useFreeTierQuery } from '../../../../hooks/useFreeTierQuery'
import { useTierActions } from '../../../../hooks/useTierActions'
import { useTierQuery } from '../../../../hooks/useTierQuery'
import { useUpdateFreeTierMutation } from '../../../../hooks/useUpdateFreeTierMutation'
import { useUpdateTierMutation } from '../../../../hooks/useUpdateTierMutation'
import type { EnabledCurrencies, FreeTier, PriceRates, Tier } from '../../../../types'
import { isPaidTier } from '../../../../utils'
import { usePriceRatesQuery } from '../../hooks/usePriceRatesQuery'
import { getValidationSchema } from './consts'
import type { FormValues } from './types'
import { FormFields } from './types'
import {
  fillCurrencyInputsWithFetchedPriceRates,
  getCreateTierPayload,
  getFreeTierPayload,
  getUpdateTierPayload,
} from './utils'

type Props = { children: ReactNode }

type TierContextType = {
  tier?: Tier | FreeTier
  form: ReturnType<typeof useFormik<FormValues>>
  annualDiscount?: number
  enabledCurrencies?: EnabledCurrencies
  priceRates?: PriceRates
  isCreatingNewTier: boolean
  isLoading: boolean
  isPriceRatesLoading: boolean
  updatedCurrencyConversion: PriceRates | null
  updateCurrencyConversion: (priceRates: PriceRates) => void
  publishTier: () => void
  unpublishTier: () => void
  deleteTier: () => void
  deleteFreeTier: () => void
}

export const TierContext = createContext<TierContextType | undefined>(undefined)

export const TierProvider = ({ children }: Props) => {
  const navigate = useNavigate()
  const { tierId = '' } = useParams<'tierId'>()

  const [updatedCurrencyConversion, setUpdatedCurrencyConversion] = useState<PriceRates | null>(
    null,
  )

  const isCreatingNewTier = tierId === ''

  const tierActions = useTierActions()

  const { freeTierQuery } = useFreeTierQuery([], {
    cacheTime: 0,
    staleTime: Infinity,
  })

  const { tierQuery } = useTierQuery([{ id: parseInt(tierId) }], {
    cacheTime: 0,
    staleTime: Infinity,
    enabled: !isCreatingNewTier && tierId !== 'free',
    onSuccess: () => {
      setUpdatedCurrencyConversion(null)
    },
  })

  const { accountSettingsQuery } = useAccountSettingsQuery()
  const { currentShopQuery } = useCurrentShopQuery()

  const { createImageResources, isLoading: createImageResourcesIsLoading } =
    useCreateImageResources()

  const { createTierMutation } = useCreateTierMutation()
  const { updateTierMutation } = useUpdateTierMutation()
  const { updateFreeTierMutation } = useUpdateFreeTierMutation()

  const annualDiscount =
    (accountSettingsQuery.data?.tiers.annual.discount.enabled &&
      accountSettingsQuery.data.tiers.annual.discount.percentOff) ||
    undefined

  const handleSubmit = (values: FormValues, imageResourceId?: number) => {
    if (parseFloat(values.price) === 0) {
      return updateFreeTierMutation.mutate([getFreeTierPayload(values, imageResourceId)], {
        onSuccess: () => {
          if (isCreatingNewTier) {
            navigate(routing.memberships.perks.self)
          } else {
            freeTierQuery.refetch()
          }
        },
      })
    }

    if (isCreatingNewTier) {
      return createTierMutation.mutate([getCreateTierPayload(values, imageResourceId)], {
        onSuccess: () => {
          navigate(routing.memberships.perks.self)
        },
      })
    }

    return updateTierMutation.mutate(
      [{ id: tierQuery?.data?.id! }, getUpdateTierPayload(values, imageResourceId)],
      {
        onSuccess: () => {
          tierQuery.refetch()
        },
      },
    )
  }

  const initialValues: FormValues = useMemo(() => {
    // Paid tier
    if (tierQuery.data && !isCreatingNewTier) {
      const hasFreeTrial = tierQuery.data?.freeTrial
      const hasOneTimeDiscount = !!(
        tierQuery.data.discount?.percentOff.monthly || tierQuery.data.discount?.percentOff.annual
      )

      return {
        [FormFields.annualDiscount]: annualDiscount,
        [FormFields.title]: tierQuery.data.name,
        [FormFields.description]: tierQuery.data.description,
        [FormFields.price]: (tierQuery.data.variants.monthly.amounts.usd.amount / 100).toString(),
        [FormFields.discount]: hasFreeTrial || hasOneTimeDiscount,
        [FormFields.discountType]: hasOneTimeDiscount ? 'oneTime' : 'trial',
        [FormFields.monthDiscount]: tierQuery.data.discount?.percentOff.monthly.toString(),
        [FormFields.yearDiscount]: tierQuery.data.discount?.percentOff.annual?.toString(),
        [FormFields.timeLimit]: !!(
          tierQuery.data.discount?.timeLimited?.startTime ||
          tierQuery.data.discount?.timeLimited?.endTime
        ),
        [FormFields.startDate]: tierQuery.data.discount?.timeLimited?.startTime
          ? new Date(tierQuery.data.discount.timeLimited.startTime)
          : undefined,
        [FormFields.endDate]: tierQuery.data.discount?.timeLimited?.endTime
          ? new Date(tierQuery.data.discount.timeLimited.endTime)
          : undefined,
        [FormFields.image]: tierQuery.data.image?.url,
        [FormFields.imageFile]: undefined,
        [FormFields.removeImage]: false,
        [FormFields.convertInternationalPricing]: tierQuery.data.pricesAutomaticallyConverted,
        [FormFields.currencies]: {
          usd: (tierQuery.data.variants.monthly.amounts.usd.amount / 100).toString(),
          cad: (tierQuery.data.variants.monthly.amounts.cad.amount / 100).toString(),
          eur: (tierQuery.data.variants.monthly.amounts.eur.amount / 100).toString(),
          gbp: (tierQuery.data.variants.monthly.amounts.gbp.amount / 100).toString(),
          aud: (tierQuery.data.variants.monthly.amounts.aud.amount / 100).toString(),
          nzd: (tierQuery.data.variants.monthly.amounts.nzd.amount / 100).toString(),
          sek: (tierQuery.data.variants.monthly.amounts.sek.amount / 100).toString(),
          nok: (tierQuery.data.variants.monthly.amounts.nok.amount / 100).toString(),
          dkk: (tierQuery.data.variants.monthly.amounts.dkk.amount / 100).toString(),
          pln: (tierQuery.data.variants.monthly.amounts.pln.amount / 100).toString(),
          jpy: tierQuery.data.variants.monthly.amounts.jpy.amount.toString(),
          myr: (tierQuery.data.variants.monthly.amounts.myr.amount / 100).toString(),
          sgd: (tierQuery.data.variants.monthly.amounts.sgd.amount / 100).toString(),
          mxn: (tierQuery.data.variants.monthly.amounts.mxn.amount / 100).toString(),
        },
        [FormFields.askForShippingAddress]: tierQuery.data.askForShippingAddress,
      }
    }

    // Free tier
    if (freeTierQuery.data && !isCreatingNewTier) {
      return {
        [FormFields.annualDiscount]: annualDiscount,
        [FormFields.title]: freeTierQuery.data.name,
        [FormFields.description]: freeTierQuery.data.description,
        [FormFields.price]: '0',
        [FormFields.discount]: false,
        [FormFields.discountType]: 'trial',
        [FormFields.monthDiscount]: undefined,
        [FormFields.yearDiscount]: undefined,
        [FormFields.timeLimit]: false,
        [FormFields.startDate]: undefined,
        [FormFields.endDate]: undefined,
        [FormFields.image]: freeTierQuery.data.image?.url,
        [FormFields.imageFile]: undefined,
        [FormFields.removeImage]: false,
        [FormFields.convertInternationalPricing]: false,
        [FormFields.currencies]: {
          usd: '',
          cad: '',
          eur: '',
          gbp: '',
          aud: '',
          nzd: '',
          sek: '',
          nok: '',
          dkk: '',
          pln: '',
          jpy: '',
          myr: '',
          sgd: '',
          mxn: '',
        },
        [FormFields.askForShippingAddress]: false,
      }
    }

    // New tier create
    return {
      [FormFields.annualDiscount]: annualDiscount,
      [FormFields.title]: '',
      [FormFields.description]: '',
      [FormFields.price]: '',
      [FormFields.discount]: false,
      [FormFields.discountType]: 'trial',
      [FormFields.monthDiscount]: undefined,
      [FormFields.yearDiscount]: undefined,
      [FormFields.timeLimit]: false,
      [FormFields.startDate]: undefined,
      [FormFields.endDate]: undefined,
      [FormFields.image]: undefined,
      [FormFields.imageFile]: undefined,
      [FormFields.removeImage]: false,
      [FormFields.convertInternationalPricing]: true,
      [FormFields.currencies]: {
        usd: '',
        cad: '',
        eur: '',
        gbp: '',
        aud: '',
        nzd: '',
        sek: '',
        nok: '',
        dkk: '',
        pln: '',
        jpy: '',
        myr: '',
        sgd: '',
        mxn: '',
      },
      [FormFields.askForShippingAddress]: false,
    }
  }, [isCreatingNewTier, tierQuery, freeTierQuery, annualDiscount])

  const form = useFormik<FormValues>({
    initialValues,
    validationSchema: getValidationSchema({
      isFreeTierCreated: freeTierQuery.data?.status !== 'Hidden',
      isEditingPaidTier: !isCreatingNewTier && tierId !== 'free',
      isCreatingNewTier,
    }),
    onReset: () => {
      setUpdatedCurrencyConversion(null)
    },
    onSubmit: (values) => {
      if (values.imageFile) {
        return createImageResources({
          files: [values.imageFile],
          onSuccess: (images) => {
            handleSubmit(values, images[0].id)
          },
        })
      }

      return handleSubmit(values)
    },
    enableReinitialize: true,
  })

  const updateCurrencyConversion = useCallback(
    (priceRates: PriceRates) => {
      setUpdatedCurrencyConversion(priceRates)
      fillCurrencyInputsWithFetchedPriceRates(form, priceRates)
    },
    [form],
  )

  const { priceRatesQuery } = usePriceRatesQuery(
    [{ usd_amount: fixRounding((parseFloat(form.values.price) || 1) * 100, 2) }],
    {
      cacheTime: 0,
      staleTime: Infinity,
      enabled: !!parseFloat(form.values.price),
      onSuccess: (priceRates) => {
        const enabledCurrencies = currentShopQuery.data?.settings.enabledCurrencies.filter(
          (currency) => currency !== 'BRL',
        )

        if (!enabledCurrencies) return

        const disabledCurrencies = Object.keys(priceRates).filter(
          (currency) =>
            !enabledCurrencies.includes(
              currency.toUpperCase() as Exclude<EnabledCurrencies[number], 'BRL'>,
            ),
        ) as (keyof typeof priceRates)[]

        // Fill all disabled currencies with fetched price rates
        if (isCreatingNewTier) {
          fillCurrencyInputsWithFetchedPriceRates(form, priceRates, disabledCurrencies)
        }

        // Fill all currency inputs with fetched price rates if user is creating new tier and "Automatically convert international pricing" is checked
        if (isCreatingNewTier && form.values.convertInternationalPricing) {
          fillCurrencyInputsWithFetchedPriceRates(form, priceRates)
        }

        if (
          tierQuery.data &&
          isPaidTier(tierQuery.data) &&
          form.values.convertInternationalPricing &&
          form.values.price !== form.initialValues.price
        ) {
          fillCurrencyInputsWithFetchedPriceRates(form, priceRates)
        }
      },
    },
  )

  const publishTier = useCallback(() => {
    if (!tierQuery.data) return

    return tierActions.publishTier(tierQuery.data, () => {
      tierQuery.refetch()
    })
  }, [tierActions, tierQuery])

  const unpublishTier = useCallback(() => {
    if (!tierQuery.data) return

    return tierActions.unpublishTier(tierQuery.data, () => {
      tierQuery.refetch()
    })
  }, [tierActions, tierQuery])

  const deleteTier = useCallback(() => {
    if (!tierQuery.data) return

    return tierActions.deleteTier(tierQuery.data, () => {
      navigate(routing.memberships.perks.self)
    })
  }, [tierActions, tierQuery, navigate])

  const deleteFreeTier = useCallback(() => {
    if (!freeTierQuery.data) return

    return tierActions.deleteFreeTier(() => {
      navigate(routing.memberships.perks.self)
    })
  }, [tierActions, navigate, freeTierQuery])

  const value = useMemo(
    () => ({
      tier: isCreatingNewTier ? undefined : tierQuery.data || freeTierQuery.data,
      form,
      annualDiscount,
      enabledCurrencies: currentShopQuery.data
        ? currentShopQuery.data?.settings.enabledCurrencies.filter(
            (currency) => currency !== 'USD' && currency !== 'BRL',
          )
        : undefined,
      priceRates: priceRatesQuery.data,
      isCreatingNewTier,
      isLoading:
        tierQuery.isFetching ||
        freeTierQuery.isFetching ||
        createTierMutation.isLoading ||
        updateTierMutation.isLoading ||
        updateFreeTierMutation.isLoading ||
        createImageResourcesIsLoading ||
        tierActions.isLoading,
      isPriceRatesLoading: priceRatesQuery.isFetching,
      updatedCurrencyConversion,
      updateCurrencyConversion,
      publishTier,
      unpublishTier,
      deleteTier,
      deleteFreeTier,
    }),
    [
      tierQuery,
      freeTierQuery,
      form,
      annualDiscount,
      currentShopQuery,
      priceRatesQuery,
      isCreatingNewTier,
      createTierMutation,
      updateTierMutation,
      updateFreeTierMutation,
      createImageResourcesIsLoading,
      tierActions,
      updatedCurrencyConversion,
      updateCurrencyConversion,
      publishTier,
      unpublishTier,
      deleteTier,
      deleteFreeTier,
    ],
  )

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>
}
